import React from "react";
import { useHistory } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import { useAsyncEffect } from "@react-hook/async";
import * as Sentry from "@sentry/react";

import { finishSamlAuth } from "@shared/api";
import { LoadingScreen, RobotsNoindex } from "@shared/components";
import { LOGIN_STATE_SENTINEL } from "@shared/constants";
import {
  getRedirectPath,
  getUserData,
  recoverBrowserSecurityError,
} from "@shared/utils";

export const SamlFinishScreen: React.FC<RouteComponentProps> = () => {
  const history = useHistory();

  useAsyncEffect(async () => {
    const urlParams: URLSearchParams = new URLSearchParams(
      history.location.search
    );

    const validateSentinel = () => {
      const receivedSentinel = (urlParams.get("state") || "").split("-")[0];
      const sentSentinel: string | null = recoverBrowserSecurityError(() =>
        localStorage.getItem(LOGIN_STATE_SENTINEL)
      );
      recoverBrowserSecurityError(() =>
        localStorage.removeItem(LOGIN_STATE_SENTINEL)
      );

      if (receivedSentinel !== sentSentinel && sentSentinel !== null) {
        history.replace("/");
        throw new Error(
          `Our sentinel value is different from what we sent to SAML in the relayState but we don't get one in IdP initiated login flow so might need to think a bit here`
        );
      }
    };

    const handleFinishAuth = async () => {
      const providerName = urlParams.get("providerName");
      const errorDesc = urlParams.get("desc");
      const error = urlParams.get("error");
      const token = urlParams.get("token");
      const authState = urlParams.get("state");

      if (!token || error) {
        const errorMsg = `error finishing SAML auth: ${
          error || "unknown error"
        }`;
        Sentry.captureMessage(errorMsg);
        history.push({
          pathname: "/sso-error",
          state: {
            error: error || "unknown_error",
            desc: errorDesc || errorMsg,
            providerName: providerName || "",
          },
        });
        return;
      }

      validateSentinel();

      try {
        await finishSamlAuth(token);
      } catch (err) {
        Sentry.captureException(err);
        console.error("could not finish user auth: " + err);
        history.push("/fatal-error");
        return;
      }
      // even though UserContext might not have had a user yet
      // we are guaranteed to get a result now after the above lines
      const user = getUserData();
      if (!user) return;

      // bring users back to wherever they signed in from
      history.push(getRedirectPath(authState));
      // refresh page so user changes are reflected
      history.go(0);
    };

    await handleFinishAuth();
  }, []); // Important: This must be empty so that callback runs ONLY once

  return (
    <>
      <RobotsNoindex />
      <LoadingScreen text="Logging in…" />
    </>
  );
};
